import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Decisao } from '@services/db.js'
import {
  addDecisao,
  deleteDecisao,
  decisoesPendentesDoCatalogo,
  decisoesQueTravemAgora,
  getCronograma,
  getDecisoes,
  resumirCronograma,
  updateDecisao,
  type NovaDecisao,
} from '@services/gestao.js'
import { getObraConfig } from '@services/crud.js'
import type { DecisaoCatalogo } from '@content/decisoes-catalogo.js'

export function useDecisoes() {
  const [registradas, setRegistradas] = useState<Decisao[]>([])
  const [faseAtualId, setFaseAtualId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(async () => {
    const [rows, fases, config] = await Promise.all([
      getDecisoes(),
      getCronograma(),
      getObraConfig(),
    ])
    setRegistradas(rows)
    setFaseAtualId(
      resumirCronograma(fases, config?.dataInicio, config?.dataPrevisaoTermino).faseAtualId ??
        config?.faseConstrutiva ??
        null,
    )
    setIsLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const pendentes = useMemo(
    () => decisoesPendentesDoCatalogo(registradas, faseAtualId),
    [registradas, faseAtualId],
  )
  const travando = useMemo(
    () => decisoesQueTravemAgora(registradas, faseAtualId),
    [registradas, faseAtualId],
  )

  const registrar = useCallback(
    async (input: NovaDecisao) => {
      await addDecisao(input)
      await load()
    },
    [load],
  )

  const editar = useCallback(
    async (id: number, patch: Partial<NovaDecisao>) => {
      await updateDecisao(id, patch)
      await load()
    },
    [load],
  )

  const remover = useCallback(
    async (id: number) => {
      await deleteDecisao(id)
      await load()
    },
    [load],
  )

  return {
    registradas,
    pendentes,
    travando,
    faseAtualId,
    isLoading,
    registrar,
    editar,
    remover,
    reload: load,
  }
}

export type { DecisaoCatalogo }
